import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Building2, Calendar, Users, Shield, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext.tsx';
import { useAlert } from '@/context/AlertContext.tsx';
import { formatDate } from '@/utils/formatDate.ts';
import CreateEditTeamModal from '@/components/team/CreateEditTeamModal.tsx';

export default function AccountInfo() {
  const { t } = useTranslation();
  const { user, refreshUser, isTrial } = useAuth();
  const { showAlert } = useAlert();
  const [showTeamModal, setShowTeamModal] = useState(false);
  const isSuperUser = user?.role === 'SuperUser';
  const isManager = user?.role === 'SalesManager' || isSuperUser;
  const managerTeams = user?.teams ?? [];
  const hasManagerTeams = managerTeams.length > 0;

  function getRoleBadge(role: string) {
    if (role === 'SuperUser') {
      return (
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-100 text-red-700">
          {t('common.roleSuperUser')}
        </span>
      );
    }
    if (isTrial) {
      return (
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
          {t('common.roleTrial', { defaultValue: 'Cuenta de prueba' })}
        </span>
      );
    }
    const isManagerRole = role === 'SalesManager';
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
          isManagerRole
            ? 'bg-purple-100 text-purple-700'
            : 'bg-blue-100 text-blue-700'
        }`}
      >
        {isManagerRole ? t('common.roleSalesManager') : t('common.roleSalesperson')}
      </span>
    );
  }

  function renderSalespersonTeam() {
    return (
      <p className="text-sm text-foreground">
        {user?.teamName ?? t('profile.noTeamSalesperson')}
      </p>
    );
  }

  function renderManagerTeams() {
    if (!hasManagerTeams) {
      return (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-muted-foreground">{t('profile.noTeamManager')}</p>
          {!isSuperUser && (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setShowTeamModal(true)}
              className="text-primary"
            >
              <Plus className="size-3" />
              <span>{t('profile.createTeam')}</span>
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        <ul className="flex flex-wrap gap-1.5">
          {managerTeams.map((team) => (
            <li
              key={team.id}
              className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary"
            >
              {team.name}
            </li>
          ))}
        </ul>
        {!isSuperUser && (
          <Link
            to="/team"
            className="text-xs font-medium text-primary hover:text-primary-hover no-underline"
          >
            {t('profile.manageTeams', {
              count: managerTeams.length,
              defaultValue: 'Gestionar equipos',
            })}
          </Link>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="mb-5 text-lg font-semibold text-foreground">
          {t('profile.accountInfo')}
        </h2>

        <div className={`grid grid-cols-1 gap-4 ${isTrial ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-4'}`}>
          {/* Role */}
          <div className="flex items-start gap-3 rounded-lg bg-muted p-4">
            <Shield className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div>
              <p className="text-xs font-medium text-muted-foreground">{t('profile.roleLabel')}</p>
              <div className="mt-1">
                {user && getRoleBadge(user.role)}
              </div>
            </div>
          </div>

          {/* Trial accounts skip Company/Team blocks — they don't belong to any tenant. */}
          {!isTrial && (
            <div className="flex items-start gap-3 rounded-lg bg-muted p-4">
              <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-xs font-medium text-muted-foreground">{t('profile.companyLabel')}</p>
                <p className="mt-1 text-sm text-foreground">
                  {user?.company ? user.company : t('profile.noCompany')}
                </p>
              </div>
            </div>
          )}

          {!isTrial && (
            <div className="flex items-start gap-3 rounded-lg bg-muted p-4">
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-xs font-medium text-muted-foreground">
                  {isManager
                    ? t('profile.teamsLabel', { defaultValue: 'Equipos' })
                    : t('profile.teamLabel')}
                </p>
                <div className="mt-1">
                  {!user
                    ? <p className="text-sm text-foreground">—</p>
                    : isManager
                      ? renderManagerTeams()
                      : renderSalespersonTeam()}
                </div>
              </div>
            </div>
          )}

          {/* Member since */}
          <div className="flex items-start gap-3 rounded-lg bg-muted p-4">
            <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div>
              <p className="text-xs font-medium text-muted-foreground">{t('profile.memberSince')}</p>
              <p className="mt-1 text-sm text-foreground">
                {user?.createdAt ? formatDate(user.createdAt) : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {isManager && !isSuperUser && (
        <CreateEditTeamModal
          isOpen={showTeamModal}
          teamId={undefined}
          currentName={undefined}
          onClose={() => setShowTeamModal(false)}
          onSaved={async () => {
            await refreshUser();
            showAlert(t('profile.teamCreated'), 'success');
          }}
        />
      )}
    </>
  );
}
